/**
 * Screen navigation.
 * Handles transitions home ↔ game. Main API: showGameScreen(), showHomeScreen(), buildScreenNavDependencies().
 */

/** Fade transition duration (ms). */
const FADE_MS = 300

import { getAudioManager, getVolumeCeilings, getSettingsDefaults } from '../../features/audio'
import { unlockAudioContext } from '../../features/audio/unlock-audio-context'
import { SaveStateManager } from '../../features/save-state'
import { initLenis } from '../../features/lenis'
import { isDebugModeEnabled } from '../../features/debug'
import { setupScrollTracking } from '../scroll-tracking'
import {
  getGlobalScrollIndicator,
  showGlobalScrollIndicator,
  hideGlobalScrollIndicator,
} from '../../utils/scroll-indicator'
import { createGlobalRainManager, setRainAudioVolume } from '../../features/rain/global-rain-manager'
import { AtmoManager, type LoopLayerConfig } from '../../features/audio/AtmoManager'
import { ScrollSfxManager, type ScrollSfxConfig } from '../../features/audio/ScrollSfxManager'
import { suppressScrollSounds, resumeScrollSounds } from '../../features/scenes/animations/scroll-sound'
import { destroyBackgroundFreeze } from '../../features/background-texture/background-freeze'
import { scrollManager } from '../../utils/scroll'

const MINIGAME_ID = 'minihra-baleni-s-rychlonozkou'
import type { StoryEngine } from '../../features/story-engine'
import type { LenisInstance } from '../../features/lenis/types'
import type { ScrollTrackingInstance } from '../scroll-tracking'
import type { ScrollRestorationInstance } from '../../features/scroll-restoration'
import type { AppUI } from '../app-orchestrator'
import { setupGameScreenFeatures } from '../game-screen-setup'
import storyData from '../../config/story.json'

export type StoryData = typeof storyData

export interface ScreenNavigationDependencies {
  app: HTMLElement
  gameScreen: HTMLElement
  storyEngine: StoryEngine
  lenisInstance: LenisInstance | null
  scrollTracking: ScrollTrackingInstance
  scrollRestoration: ScrollRestorationInstance
  ui: AppUI
  storyData: StoryData
  setupGameScreenFeatures: typeof setupGameScreenFeatures
  updateScrollTracking: () => void
  initLenis: () => LenisInstance | null
  destroyLenis: () => void
  isDebugModeEnabled: () => boolean
  isTransitioning: { value: boolean }
  isRestoringScroll: { value: boolean }
  state: ScreenNavDepsState
}

export interface ScreenNavDepsState {
  lenisInstance: LenisInstance | null
  isTransitioning: { value: boolean }
  isRestoringScroll: { value: boolean }
  globalRainManager: ReturnType<typeof createGlobalRainManager> | null
  atmoManager: AtmoManager | null
  musicManager: AtmoManager | null
  scrollSfxManager: ScrollSfxManager | null
}

/**
 * Build screen navigation dependencies (for use from main.ts).
 */
export function buildScreenNavDependencies(
  app: HTMLElement,
  state: ScreenNavDepsState,
  gameScreen: HTMLElement,
  storyEngine: StoryEngine,
  scrollTracking: ScrollTrackingInstance,
  scrollRestoration: ScrollRestorationInstance,
  ui: AppUI,
): ScreenNavigationDependencies {
  const updateScrollTracking = () => {
    scrollTracking.stop()
    const newTracking = setupScrollTracking(state.lenisInstance?.lenis ?? null, {
      onScroll: (scrollY) => storyEngine.saveCurrentState(scrollY),
    })
    Object.assign(scrollTracking, newTracking)
  }

  return {
    app,
    gameScreen,
    storyEngine,
    lenisInstance: state.lenisInstance,
    scrollTracking,
    scrollRestoration,
    ui,
    storyData,
    setupGameScreenFeatures,
    updateScrollTracking,
    initLenis: () => {
      if (!state.lenisInstance) state.lenisInstance = initLenis(storyData)
      return state.lenisInstance
    },
    destroyLenis: () => {
      if (state.lenisInstance) {
        state.lenisInstance.destroy()
        state.lenisInstance = null
      }
    },
    isDebugModeEnabled,
    isTransitioning: state.isTransitioning,
    isRestoringScroll: state.isRestoringScroll,
    state,
  }
}

// --- Fade overlay helpers ---

function getFadeOverlay(app: HTMLElement): HTMLDivElement {
  const el = app.querySelector('.screen-transition') as HTMLDivElement
  if (!el) throw new Error('Screen transition element not found in HTML')
  return el
}

function fadeToBlack(overlay: HTMLDivElement): void {
  overlay.classList.remove('opacity-visible')
  overlay.classList.add('opacity-hidden')
  void overlay.offsetHeight // force reflow
  requestAnimationFrame(() => {
    overlay.classList.remove('opacity-hidden')
    overlay.classList.add('opacity-visible')
  })
}

function fadeFromBlack(overlay: HTMLDivElement): void {
  overlay.classList.remove('opacity-visible')
}

function hideOverlayAfter(overlay: HTMLDivElement, ms: number, onDone?: () => void): void {
  setTimeout(() => {
    overlay.classList.add('opacity-hidden')
    onDone?.()
  }, ms)
}

// --- Game screen visibility ---

function hideHomeScreenAndLogo(app: HTMLElement, ui: AppUI): void {
  ui.homeScreen.hide()
  const logo = app.querySelector('.home-screen__logo') as HTMLElement | null
  if (logo) logo.classList.add('hidden')
}

function showGameScreenAndNav(
  gameScreen: HTMLElement,
  ui: AppUI,
  showBackButton: boolean,
  showDebugButton: boolean,
): void {
  gameScreen.classList.remove('hidden')
  ui.gameNavigation.show(showBackButton, showDebugButton)
}

function showLogo(app: HTMLElement): void {
  const logo = app.querySelector('.home-screen__logo') as HTMLElement | null
  if (logo) logo.classList.remove('hidden')
}

// --- Scroll and reveal (after game content is ready) ---

function restoreScrollThenReveal(
  deps: ScreenNavigationDependencies,
  savedState: NonNullable<ReturnType<typeof SaveStateManager.load>>,
  lenisInstance: ScreenNavigationDependencies['lenisInstance'],
  overlay: HTMLDivElement,
  onTransitionComplete: () => void,
): void {
  const { scrollTracking, scrollRestoration, updateScrollTracking, isRestoringScroll } = deps
  isRestoringScroll.value = true
  scrollTracking.setRestoring(true)

  scrollRestoration.restore({
    targetScroll: savedState.scrollPosition,
    lenisInstance: lenisInstance?.lenis ?? null,
    onComplete: () => {
      updateScrollTracking()
      scrollTracking.start()
      setTimeout(() => {
        isRestoringScroll.value = false
        scrollTracking.setRestoring(false)
      }, 200)
    },
  })

  fadeFromBlack(overlay)
  hideOverlayAfter(overlay, FADE_MS, onTransitionComplete)
}

function scrollToTopThenReveal(
  deps: ScreenNavigationDependencies,
  lenisInstance: ScreenNavigationDependencies['lenisInstance'],
  overlay: HTMLDivElement,
  onTransitionComplete: () => void,
): void {
  const { scrollRestoration, updateScrollTracking, scrollTracking } = deps
  scrollRestoration.scrollToTop(lenisInstance?.lenis ?? null)
  updateScrollTracking()
  scrollTracking.start()

  fadeFromBlack(overlay)
  hideOverlayAfter(overlay, FADE_MS, onTransitionComplete)
}

/**
 * Show game screen with transition.
 * Flow: fade to black → (after 300ms) update buttons if needed → hide home, show story →
 *       init Lenis & features → start engine with scroll/reveal.
 */
export function showGameScreen(
  dependencies: ScreenNavigationDependencies,
  loadState: boolean = false,
  updateButtonsBeforeFade: boolean = false,
): void {
  // Unlock AudioContext while still inside the user-gesture call stack.
  // The silent-buffer trick is required on iPadOS where ctx.resume() alone
  // is not sufficient; after the setTimeout the gesture expires.
  unlockAudioContext()

  const deps = dependencies
  deps.isTransitioning.value = true
  const savedState = loadState ? SaveStateManager.load() : null
  const overlay = getFadeOverlay(deps.app)

  fadeToBlack(overlay)
  setTimeout(() => afterFadeToGameScreen(deps, loadState, savedState, overlay, updateButtonsBeforeFade), FADE_MS)
}

function afterFadeToGameScreen(
  deps: ScreenNavigationDependencies,
  loadState: boolean,
  savedState: ReturnType<typeof SaveStateManager.load>,
  overlay: HTMLDivElement,
  updateButtonsBeforeFade: boolean,
): void {
  const { app, gameScreen, storyEngine, ui, isTransitioning, isDebugModeEnabled } = deps

  if (updateButtonsBeforeFade) ui.homeScreen.updateButtons()
  hideHomeScreenAndLogo(app, ui)
  showGameScreenAndNav(gameScreen, ui, true, isDebugModeEnabled())

  const lenisInstance = deps.initLenis()
  if (lenisInstance) {
    deps.setupGameScreenFeatures(lenisInstance, storyEngine, deps.storyData, app)
    deps.ui.storyNav.setVisible(true)
    if (!loadState) deps.ui.storyNav.resetProgress()
    const gameScreen = deps.gameScreen
    scrollManager.init()

    // Setup intro screen scroll indicator
    setupIntroScrollIndicator(gameScreen, lenisInstance)
    const chapterIds = (deps.storyData.scenes as Array<{ type?: string; id: string }>)
      .filter((s) => s.type === 'chapter-title')
      .map((s) => s.id)
    const vh = () => window.innerHeight
    const vhHalf = () => vh() / 2
    const wrappers = () => Array.from(gameScreen.querySelectorAll('.scene-wrapper'))
    const chapterIdSet = new Set(chapterIds)

    function getSceneCoveringMostViewport(): { wrapper: HTMLElement; listIndex: number } | null {
      const list = wrappers()
      let maxOverlap = 0
      let best: { wrapper: HTMLElement; listIndex: number } | null = null
      for (let j = 0; j < list.length; j++) {
        const rect = list[j].getBoundingClientRect()
        const overlap = Math.max(0, Math.min(rect.bottom, vh()) - Math.max(rect.top, 0))
        if (overlap > maxOverlap) {
          maxOverlap = overlap
          best = { wrapper: list[j] as HTMLElement, listIndex: j }
        }
      }
      return best
    }

    deps.ui.storyNav.start({
      getRevealedChapterIndices: () => {
        return chapterIds
          .map((id, i) => {
            const el = gameScreen.querySelector(`[data-scene="${id}"]`)
            if (!el) return -1
            const rect = el.getBoundingClientRect()
            const centerY = rect.top + rect.height / 2
            return centerY <= vhHalf() ? i : -1
          })
          .filter((i) => i >= 0)
      },
      getRevealedSegmentIndices: () => {
        const list = wrappers()
        const revealed: number[] = []
        for (let seg = 0; seg < chapterIds.length - 1; seg++) {
          const chapterWrapper = gameScreen.querySelector(`[data-scene="${chapterIds[seg]}"]`)
          if (!chapterWrapper) continue
          const idx = list.indexOf(chapterWrapper as HTMLElement)
          if (idx < 0 || idx >= list.length - 1) continue
          const firstSceneAfter = list[idx + 1]
          const rect = firstSceneAfter.getBoundingClientRect()
          const centerY = rect.top + rect.height / 2
          if (centerY <= vhHalf()) revealed.push(seg)
        }
        return revealed
      },
      getCurrentChapterIndex: () => {
        const hit = getSceneCoveringMostViewport()
        if (!hit) return -1
        const sceneId = hit.wrapper.getAttribute('data-scene')
        if (!sceneId || !chapterIdSet.has(sceneId)) return -1
        return chapterIds.indexOf(sceneId)
      },
      getCurrentSegmentIndex: () => {
        const hit = getSceneCoveringMostViewport()
        if (!hit) return -1
        const sceneId = hit.wrapper.getAttribute('data-scene')
        if (sceneId && chapterIdSet.has(sceneId)) return -1
        const list = wrappers()
        for (let k = hit.listIndex - 1; k >= 0; k--) {
          const id = (list[k] as HTMLElement).getAttribute('data-scene')
          if (id && chapterIdSet.has(id)) return chapterIds.indexOf(id)
        }
        return -1
      },
      getCurrentSceneIndex: () => {
        const hit = getSceneCoveringMostViewport()
        if (!hit) return -1
        const sceneId = hit.wrapper.getAttribute('data-scene')
        if (!sceneId) return -1
        // Find the scene index in the story data
        const sceneIndex = deps.storyData.scenes.findIndex((s) => s.id === sceneId)
        return sceneIndex >= 0 ? sceneIndex : -1
      },
      scrollToChapter: (chapterIndex: number) => {
        const id = chapterIds[chapterIndex]
        if (!id) return
        const el = gameScreen.querySelector(`[data-scene="${id}"]`)
        if (!el) return
        const rect = el.getBoundingClientRect()
        const scrollY = lenisInstance.lenis?.scroll ?? window.scrollY
        const topInDoc = rect.top + scrollY
        const targetScroll = Math.max(0, topInDoc + rect.height / 2 - vhHalf())
        suppressScrollSounds()
        if (lenisInstance.lenis) {
          lenisInstance.lenis.scrollTo(targetScroll, { duration: 0.8, onComplete: resumeScrollSounds })
        } else {
          window.scrollTo({ top: targetScroll, behavior: 'smooth' })
          setTimeout(resumeScrollSounds, 1000)
        }
      },
    })
  }

  const onRevealComplete = () => {
    isTransitioning.value = false

    // Initialize atmosphere and music audio managers from story.json config.audio
    const audioConfig = (
      deps.storyData as unknown as {
        config?: {
          audio?: {
            atmo?: LoopLayerConfig
            music?: LoopLayerConfig
            sfx?: ScrollSfxConfig[]
            global?: { intro?: string; outro?: string }
            rain?: { soft?: string; strong?: string }
          }
        }
      }
    )?.config?.audio

    // Play intro sound when the intro screen is visible
    const introEl = gameScreen.querySelector('.game-screen__intro') as HTMLElement | null
    if (introEl && audioConfig?.global?.intro) {
      const rect = introEl.getBoundingClientRect()
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        getAudioManager().playGlobalSound(audioConfig.global.intro)
      }
    }

    // Initialize global rain manager after scenes are loaded (config from story.json effects.rain)
    if (!deps.state.globalRainManager) {
      const rainConfig = (
        deps.storyData as {
          config?: {
            effects?: {
              rain?: { from?: string[]; to?: string[]; minDrops?: number; maxDrops?: number; dropAngle?: number }
            }
          }
        }
      )?.config?.effects?.rain
      const rainSfxCfg =
        audioConfig?.rain?.soft && audioConfig?.rain?.strong
          ? { soft: audioConfig.rain.soft, strong: audioConfig.rain.strong }
          : undefined
      deps.state.globalRainManager = createGlobalRainManager(
        rainConfig?.from?.length === 2 && rainConfig?.to?.length === 2
          ? {
              from: rainConfig.from as [string, string],
              to: rainConfig.to as [string, string],
              minDrops: rainConfig.minDrops,
              maxDrops: rainConfig.maxDrops,
              dropAngle: rainConfig.dropAngle,
            }
          : undefined,
        rainSfxCfg,
      )
      deps.state.globalRainManager.init()
    }

    const storySceneIds = (deps.storyData.scenes as Array<{ id: string }>).map((s) => s.id)

    if (!deps.state.atmoManager && audioConfig?.atmo?.tracks?.length) {
      deps.state.atmoManager = new AtmoManager(audioConfig.atmo, '/assets/audio/atmo', storySceneIds)
      deps.state.atmoManager.init(gameScreen)
    }

    if (!deps.state.musicManager && audioConfig?.music?.tracks?.length) {
      deps.state.musicManager = new AtmoManager(audioConfig.music, '/assets/audio/music', storySceneIds)
      deps.state.musicManager.init(gameScreen)
    }

    if (!deps.state.scrollSfxManager && audioConfig?.sfx?.length) {
      deps.state.scrollSfxManager = new ScrollSfxManager(audioConfig.sfx as ScrollSfxConfig[])
      deps.state.scrollSfxManager.init(gameScreen)
    }

    // Apply saved volume settings to all managers (scaled by ceilings)
    const ceilings = getVolumeCeilings()
    const defaults = getSettingsDefaults()
    const savedSettings = localStorage.getItem('rychle-sipy-a-velikonocni-poklad:settings')
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings)
        const musicUserScale = (parsed?.sound?.music ?? defaults.music) / 100
        const soundVol = ((parsed?.sound?.sound ?? defaults.sound) / 100) * ceilings.sound
        deps.state.atmoManager?.setVolume(musicUserScale * ceilings.atmo)
        deps.state.musicManager?.setVolume(musicUserScale * ceilings.music)
        setRainAudioVolume(soundVol)
        deps.state.scrollSfxManager?.setVolume(soundVol)
        if ((deps.state as any).sfxManager) (deps.state as any).sfxManager.setVolume(soundVol)
      } catch {
        /* ignore */
      }
    } else {
      const defMusicUserScale = defaults.music / 100
      const defSoundVol = (defaults.sound / 100) * ceilings.sound
      deps.state.atmoManager?.setVolume(defMusicUserScale * ceilings.atmo)
      deps.state.musicManager?.setVolume(defMusicUserScale * ceilings.music)
      setRainAudioVolume(defSoundVol)
      deps.state.scrollSfxManager?.setVolume(defSoundVol)
      if ((deps.state as any).sfxManager) (deps.state as any).sfxManager.setVolume(defSoundVol)
    }
  }
  const setScrollAndShow = () => {
    if (loadState && savedState) {
      restoreScrollThenReveal(deps, savedState, lenisInstance ?? null, overlay, onRevealComplete)
    } else {
      scrollToTopThenReveal(deps, lenisInstance ?? null, overlay, onRevealComplete)
    }
  }

  if (loadState && savedState) {
    storyEngine.start(savedState, setScrollAndShow)
  } else {
    storyEngine.start(undefined, setScrollAndShow)
  }
}

/**
 * Setup scroll indicator for intro screen
 * Shows indicator when intro is visible, hides when scrolled past
 * Never shows if minigame is completed
 */
function setupIntroScrollIndicator(gameScreen: HTMLElement, lenisInstance: LenisInstance): void {
  const introElement = gameScreen.querySelector('.game-screen__intro') as HTMLElement
  if (!introElement) return

  const indicator = getGlobalScrollIndicator()

  function isMinigameCompleted(): boolean {
    const saved = SaveStateManager.load()
    const minigame = saved?.minigames?.[MINIGAME_ID]
    return minigame?.completed === true
  }

  function isMinigameSceneVisible(): boolean {
    const minigameScene = gameScreen.querySelector(`[data-scene="${MINIGAME_ID}"]`) as HTMLElement
    if (!minigameScene) return false

    const rect = minigameScene.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    // Check if minigame scene is visible (at least partially in viewport)
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    return visibleHeight > 0
  }

  function checkIntroVisibility(): void {
    if (!introElement) return

    // If minigame scene is visible, let the minigame's own logic control the indicator
    if (isMinigameSceneVisible()) return

    const rect = introElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    const isVisible = visibleHeight >= viewportHeight * 0.5 && rect.top >= 0

    if (!isVisible) {
      hideGlobalScrollIndicator()
      return
    }

    if (isMinigameCompleted()) {
      hideGlobalScrollIndicator()
    } else {
      showGlobalScrollIndicator()
    }
  }

  // Check on scroll
  const handleScroll = () => {
    checkIntroVisibility()
  }

  // Check on resize
  const handleResize = () => {
    checkIntroVisibility()
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleResize, { passive: true })

  // Also listen to Lenis scroll events
  if (lenisInstance?.lenis) {
    lenisInstance.lenis.on('scroll', handleScroll)
  }

  // Initial check
  checkIntroVisibility()

  // Store cleanup function
  ;(gameScreen as any).__introScrollIndicatorCleanup = () => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
    if (lenisInstance?.lenis) {
      lenisInstance.lenis.off('scroll', handleScroll)
    }
    hideGlobalScrollIndicator()
  }
}

/**
 * Show home screen with transition.
 * Flow: fade out audio → save state, stop tracking → fade in overlay → (after 300ms) hide story, destroy Lenis → show home → fade from black.
 */
export function showHomeScreen(dependencies: ScreenNavigationDependencies): void {
  const deps = dependencies
  getAudioManager().fadeOutAll()
  deps.isTransitioning.value = true
  const scrollY = deps.state.lenisInstance?.lenis?.scroll ?? window.scrollY
  deps.storyEngine.saveCurrentStateImmediate(scrollY)
  deps.scrollTracking.stop()

  const overlay = getFadeOverlay(deps.app)
  overlay.classList.remove('opacity-hidden')
  requestAnimationFrame(() => {
    overlay.classList.add('opacity-visible')
    setTimeout(() => afterFadeToHomeScreen(deps, overlay), FADE_MS)
  })
}

function teardownGameScreen(deps: ScreenNavigationDependencies): void {
  const { app, gameScreen, state } = deps

  deps.ui.storyNav.stop()
  deps.ui.storyNav.setVisible(false)
  deps.destroyLenis()

  const bgInstance = (window as any).__backgroundTextureInstance as { destroy: () => void } | undefined
  if (bgInstance) {
    bgInstance.destroy()
    ;(window as any).__backgroundTextureInstance = null
  }

  const parallaxInstance = (window as any).__parallaxInstance as { destroy: () => void } | undefined
  if (parallaxInstance) {
    parallaxInstance.destroy()
    ;(window as any).__parallaxInstance = null
  }

  hideGlobalScrollIndicator()

  if (state.globalRainManager) {
    state.globalRainManager.destroy()
    state.globalRainManager = null
  }

  if (state.atmoManager) {
    state.atmoManager.destroy()
    state.atmoManager = null
  }

  if (state.musicManager) {
    state.musicManager.destroy()
    state.musicManager = null
  }

  if (state.scrollSfxManager) {
    state.scrollSfxManager.destroy()
    state.scrollSfxManager = null
  }

  if ((gameScreen as any).__introScrollIndicatorCleanup) {
    ;(gameScreen as any).__introScrollIndicatorCleanup()
    delete (gameScreen as any).__introScrollIndicatorCleanup
  }

  scrollManager.destroy()
  destroyBackgroundFreeze()
  document.body.style.backgroundImage = 'none'
  document.body.style.overflow = ''

  // Remove any open homework overlays (appended to #app, not .game-screen)
  app.querySelectorAll('.homework-overlay').forEach((el) => el.remove())
  gameScreen.style.transform = ''

  const chapterOverlay = app.querySelector('.chapter-title-overlay') as HTMLElement | null
  if (chapterOverlay) chapterOverlay.style.opacity = '0'
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

function afterFadeToHomeScreen(deps: ScreenNavigationDependencies, overlay: HTMLDivElement): void {
  const { app, gameScreen, ui, isTransitioning } = deps

  gameScreen.classList.add('hidden')
  ui.gameNavigation.hide()

  teardownGameScreen(deps)

  ui.homeScreen.show()
  ui.gameNavigation.show(false, false)
  showLogo(app)

  fadeFromBlack(overlay)
  hideOverlayAfter(overlay, FADE_MS, () => {
    isTransitioning.value = false
    ui.homeScreen.updateButtons()
  })
}

/**
 * Restart game with transition (from within the game screen).
 * Flow: fade out audio → fade to black → teardown current game → clear state → start fresh → fade from black.
 */
export function restartGame(dependencies: ScreenNavigationDependencies): void {
  const deps = dependencies
  getAudioManager().fadeOutAll()
  deps.isTransitioning.value = true
  deps.scrollTracking.stop()

  const overlay = getFadeOverlay(deps.app)
  fadeToBlack(overlay)

  setTimeout(() => {
    teardownGameScreen(deps)
    SaveStateManager.clear()
    afterFadeToGameScreen(deps, false, null, overlay, false)
  }, FADE_MS)
}
